import React from 'react';
import PropTypes from 'prop-types';
import {ButtonWithIcon, CardSkeletonLoader} from '@/components/atoms';
import {Accordion, AccordionItem} from '@/components/molecules';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {ROUTES} from '@/utils/constants/routesConstants';
import {formatActivityTypeForReview} from '@/helpers/activityUtils';
import ActivitySegmentsReviewSection from '@/containers/Activity/ActivityReview/components/ActivitySegmentsReviewSection';

const SURVEY_SPACING = {
  accordionGapClass: 'space-y-5',
  threeColGridClass: 'grid-cols-3 gap-6'
};

const formatDateYYYYMMDD = (value, getFormattedDate) =>
  getFormattedDate(value, false, '-', 'yyyy-mm-dd');

const formatCustomerTypeValues = (customerType) => {
  if (!Array.isArray(customerType) || customerType.length === 0) return '-';
  return customerType.join(', ');
};

const SurveyReviewGridRow = ({items, dataTestId, preserveRhythm = false}) => (
  <div
    className={`mt-3 grid ${SURVEY_SPACING.threeColGridClass}`}
    data-testid={dataTestId}>
    {items.map((item, idx) =>
      item?.isSpacer ? (
        <div
          key={`spacer-${idx}`}
          className='invisible pb-2'
          aria-hidden='true'
          data-testid={`${dataTestId}-spacer`}
        />
      ) : (
        <div key={item.label} className='pb-2'>
          <p className='pl-1 text-sm text-[#D0D0D0]'>{item.label}</p>
          <p className='pl-1 font-bold text-white'>{item.value}</p>
        </div>
      )
    )}
    {preserveRhythm &&
      items.length < 3 &&
      Array.from({length: 3 - items.length}).map((_, idx) => (
        <div
          key={`auto-spacer-${idx}`}
          className='invisible pb-2'
          aria-hidden='true'
          data-testid={`${dataTestId}-auto-spacer`}
        />
      ))}
  </div>
);

SurveyReviewGridRow.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.node,
      isSpacer: PropTypes.bool
    })
  ).isRequired,
  dataTestId: PropTypes.string,
  preserveRhythm: PropTypes.bool
};

const SurveyReviewSingleRow = ({label, value, dataTestId}) => (
  <SurveyReviewGridRow
    dataTestId={dataTestId}
    preserveRhythm
    items={[{label, value}]}
  />
);

SurveyReviewSingleRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  dataTestId: PropTypes.string
};

const SurveySectionTitle = ({label, className = ''}) => (
  <div className={className}>
    <h3 className='text-base font-bold text-white'>{label}</h3>
    <div
      className='mt-2 border-b border-[color:var(--color-border-divider)]'
      data-testid={`survey-${label.toLowerCase().replace(/\s+/g, '-')}-divider`}
    />
  </div>
);

SurveySectionTitle.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string
};

const SurveyActivityReview = ({
  loading,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  isEditable,
  editButtonSchema,
  navigate,
  activityFormData,
  isExcluded,
  surveyUrl,
  getFormattedDate
}) => (
  <div data-testid='survey-review' className='survey-review-container w-full'>
    {loading && <CardSkeletonLoader count={3} />}
    {!loading && (
      <>
        {showToast && (
          <DuplicateAlert
            type='info'
            message={toastMessage}
            show={true}
            onCloseClick={handleToastClose}
            handleClick={handleViewActivity}
            buttonText='View Activity'
          />
        )}
        <div className='sales-target-review mt-2 w-full'>
          <Accordion
            isDark
            size='large'
            content={
              <div className={`${SURVEY_SPACING.accordionGapClass} w-full`}>
                <AccordionItem
                  isDark
                  expanded
                  label='Setup'
                  rightAccessoryChild={
                    isEditable ? (
                      <ButtonWithIcon
                        uiSchema={{
                          ...editButtonSchema
                        }}
                        iconLeading='edit'
                        handleButton={() => navigate(ROUTES.ACTIVITY_ADD)}
                      />
                    ) : null
                  }
                  content={
                    <div className='w-full'>
                      <SurveySectionTitle
                        label='Activity Setup'
                        className='mb-3'
                      />
                      <SurveyReviewGridRow
                        dataTestId='survey-setup-grid-row'
                        preserveRhythm
                        items={[
                          {
                            label: 'Activity Type',
                            value: formatActivityTypeForReview(
                              activityFormData?.activityType
                            )
                          },
                          {
                            label: 'Survey Name',
                            value: activityFormData?.activityName || '-'
                          },
                          {
                            label: 'Points Earned',
                            value: activityFormData?.pointsEarned || '-'
                          },
                          {
                            label: 'All Time Limit',
                            value:
                              activityFormData?.allTimeLimit != null &&
                              activityFormData.allTimeLimit !== ''
                                ? String(activityFormData.allTimeLimit)
                                : '-'
                          },
                          {
                            label: 'Display Order',
                            value:
                              activityFormData?.displayOrder !== '' &&
                              activityFormData?.displayOrder !== null &&
                              activityFormData?.displayOrder !== undefined
                                ? String(activityFormData.displayOrder)
                                : '0'
                          }
                        ]}
                      />

                      <SurveySectionTitle
                        label='Availability'
                        className='mb-3 mt-6'
                      />
                      <SurveyReviewGridRow
                        dataTestId='survey-availability-grid-row'
                        items={[
                          {
                            label: 'Active Start Date',
                            value: formatDateYYYYMMDD(
                              activityFormData?.startDate,
                              getFormattedDate
                            )
                          },
                          {
                            label: 'Active End Date',
                            value: formatDateYYYYMMDD(
                              activityFormData?.endDate,
                              getFormattedDate
                            )
                          }
                        ]}
                      />

                      <ActivitySegmentsReviewSection
                        activityFormData={activityFormData}
                        isExcluded={isExcluded}
                        RowComponent={SurveyReviewSingleRow}
                        SectionTitleComponent={SurveySectionTitle}
                        testIdPrefix='survey'
                        showAllCustomersRow
                        showExcludedRow
                      />
                    </div>
                  }
                />
                <AccordionItem
                  isDark
                  expanded
                  label='Attributes'
                  rightAccessoryChild={
                    isEditable ? (
                      <ButtonWithIcon
                        uiSchema={{
                          ...editButtonSchema
                        }}
                        iconLeading='edit'
                        handleButton={() =>
                          navigate(ROUTES.ACTIVITY_ATTRIBUTES)
                        }
                      />
                    ) : null
                  }
                  content={
                    <div className='w-full'>
                      <SurveySectionTitle label='Attributes' className='mb-3' />
                      <SurveyReviewSingleRow
                        label='Customer Type'
                        value={formatCustomerTypeValues(
                          activityFormData?.customerType
                        )}
                        dataTestId='survey-customer-type-row'
                      />
                      <SurveyReviewSingleRow
                        label='Survey URL'
                        value={
                          surveyUrl ? (
                            <a
                              href={surveyUrl}
                              target='_blank'
                              rel='noreferrer'
                              className='text-sky-500 underline'>
                              {surveyUrl}
                            </a>
                          ) : (
                            '-'
                          )
                        }
                        dataTestId='survey-url-row'
                      />
                    </div>
                  }
                />
              </div>
            }
          />
        </div>
      </>
    )}
  </div>
);

SurveyActivityReview.propTypes = {
  loading: PropTypes.bool.isRequired,
  showToast: PropTypes.bool.isRequired,
  toastMessage: PropTypes.string,
  handleToastClose: PropTypes.func.isRequired,
  handleViewActivity: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  editButtonSchema: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  activityFormData: PropTypes.object.isRequired,
  isExcluded: PropTypes.bool.isRequired,
  surveyUrl: PropTypes.string.isRequired,
  getFormattedDate: PropTypes.func.isRequired
};

export default React.memo(SurveyActivityReview);
